import { AvcorePublishStreamApiCredentials, StreamId } from "@common/domain";
import { BehaviorSubject, combineLatest, EMPTY, of, Subject } from "rxjs";
import {
  distinctUntilChanged,
  filter,
  map,
  switchMap,
  takeUntil,
  tap
} from "rxjs/operators";
import { StreamData } from "../../actions";
import { ExtendedMediaStream } from "../../extended-media-stream";
import {
  ScreenShareDiagnostics,
  ScreenShareDiagnosticsOptions
} from "../diagnostics/ScreenShareDiagnostics";
import { LocalConferenceStream } from "./LocalConferenceStream";

export class LocalScreenConferenceStream extends LocalConferenceStream {
  private readonly _endHandler$ = new BehaviorSubject<null | EndHandler>(null);
  private readonly _destroy1$ = new Subject<void>();
  // YOUM-253: evidence for black screen shares, see ScreenShareDiagnostics.
  private readonly _diagnostics: ScreenShareDiagnostics;

  constructor(
    streamId: StreamId,
    stream: ExtendedMediaStream,
    logger: (...args: unknown[]) => void,
    diagnosticsOptions: Partial<ScreenShareDiagnosticsOptions> = {}
  ) {
    super(streamId, logger);

    this._diagnostics = new ScreenShareDiagnostics(
      streamId,
      logger,
      diagnosticsOptions
    );

    this._stream$
      .pipe(
        switchMap((_) =>
          _ ? _.videoTracks$.pipe(map((_) => _[0] || null)) : of(null)
        ),
        distinctUntilChanged(),
        takeUntil(this._destroy1$)
      )
      .subscribe((_) => this._diagnostics.attachTrack(_));

    // Reuses the stats ConferenceApi already collects, no extra getStats().
    this.stats$
      .pipe(takeUntil(this._destroy1$))
      .subscribe((_) => this._diagnostics.onStats(_[0]));

    this._stream$
      .pipe(
        switchMap((_) =>
          _
            ? combineLatest([
                _.videoTracks$.pipe(map((_) => _[0])),
                this._endHandler$
              ]).pipe(
                filter((_): _ is [MediaStreamTrack, EndHandler] =>
                  _.every((_) => !!_)
                ),
                tap((_) => (_[0].onended = _[1]))
              )
            : EMPTY
        ),
        takeUntil(this._destroy1$)
      )
      .subscribe();

    this._stream$.next(stream);
  }

  override async publish(
    streamData: StreamData,
    streamApiCredentials: AvcorePublishStreamApiCredentials
  ) {
    this._diagnostics.publishStart();
    try {
      const result = await super.publish(streamData, streamApiCredentials);
      if (result) {
        this._diagnostics.publishSuccess();
      } else {
        this._diagnostics.publishFailure(new Error("No stream to publish"));
      }
      return result;
    } catch (error) {
      this._diagnostics.publishFailure(error);
      throw error;
    }
  }

  override async destroy() {
    this._destroy1$.next();
    this._destroy1$.complete();

    this._diagnostics.stop();

    this._endHandler$.complete();

    await super.destroy();
  }

  override async audioDisable() {
    await super.audioDisable();
  }

  override async audioEnable() {
    await super.audioEnable();
  }

  setEndHandler(handler: EndHandler) {
    this._endHandler$.next(handler);
  }

  // YOUM-268: sent with Report Problem.
  diagnosticsSnapshot() {
    return this._diagnostics.snapshot();
  }
}

type EndHandler = () => void;
